export const avatarMixin = {
  computed: {
    apiBaseUrl() {
      return process.env.VUE_APP_API_URL?.replace('/api/v1', '') || 'http://127.0.0.1:8000';
    },

    apiOrigin() {
      try {
        return new URL(this.apiBaseUrl).origin;
      } catch (e) {
        return this.apiBaseUrl;
      }
    }
  },

  methods: {
    withCacheBuster(url, version) {
      if (!url || !version) return url;
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('v', String(version));

        // Preserve relative URLs as relative.
        if (!/^https?:\/\//i.test(url)) {
          return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }

        return parsed.toString();
      } catch (e) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}v=${encodeURIComponent(String(version))}`;
      }
    },

    rewriteStorageUrlToApiOrigin(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        const apiOrigin = this.apiOrigin;
        const currentOrigin = window.location.origin;

        const isFrontendLocalhost = parsed.origin === currentOrigin;
        const isLocalhostLike = ['localhost', '127.0.0.1'].includes(parsed.hostname);
        const shouldRewriteOrigin = parsed.origin !== apiOrigin && (isFrontendLocalhost || isLocalhostLike);

        if (!shouldRewriteOrigin) {
          return parsed.toString();
        }

        if (parsed.pathname.startsWith('/api/v1/storage/')) {
          return `${apiOrigin}${parsed.pathname.replace('/api/v1', '')}${parsed.search}${parsed.hash}`;
        }

        if (parsed.pathname.startsWith('/api/storage/')) {
          return `${apiOrigin}${parsed.pathname.replace('/api', '')}${parsed.search}${parsed.hash}`;
        }

        if (parsed.pathname.startsWith('/storage/')) {
          return `${apiOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
        }

        return parsed.toString();
      } catch (e) {
        return url;
      }
    },

    resolveAvatarSource(user) {
      const currentUser = user || this.user || {};
      const candidates = [
        currentUser.avatar,
        currentUser.avatar_url,
        currentUser.profile_image,
        currentUser.image_url,
        currentUser.profile_photo_url
      ];

      for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
      }

      return null;
    },

    getAvatarUrl(avatar, fallbackName = 'User') {
      if (!avatar) {
        return this.generateFallbackAvatar(fallbackName);
      }

      const normalizedAvatar = typeof avatar === 'string' ? avatar.trim() : avatar;
      if (typeof normalizedAvatar !== 'string') {
        return this.generateFallbackAvatar(fallbackName);
      }

      if (normalizedAvatar.startsWith('data:') || normalizedAvatar.startsWith('blob:')) {
        return normalizedAvatar;
      }

      if (normalizedAvatar.startsWith('http://') || normalizedAvatar.startsWith('https://')) {
        return this.rewriteStorageUrlToApiOrigin(normalizedAvatar);
      }

      if (normalizedAvatar.startsWith('/api/v1/storage/')) {
        return `${this.apiBaseUrl}${normalizedAvatar.replace('/api/v1', '')}`;
      }

      if (normalizedAvatar.startsWith('/api/storage/')) {
        return `${this.apiBaseUrl}${normalizedAvatar.replace('/api', '')}`;
      }

      if (normalizedAvatar.startsWith('api/v1/storage/')) {
        return `${this.apiBaseUrl}/${normalizedAvatar.replace('api/v1/', '')}`;
      }

      if (normalizedAvatar.startsWith('api/storage/')) {
        return `${this.apiBaseUrl}/${normalizedAvatar.replace('api/', '')}`;
      }

      if (normalizedAvatar.startsWith('/storage/')) {
        return `${this.apiBaseUrl}${normalizedAvatar}`;
      }

      if (normalizedAvatar.startsWith('storage/') || normalizedAvatar.startsWith('uploads/')) {
        return `${this.apiBaseUrl}/${normalizedAvatar}`;
      }

      if (normalizedAvatar.includes('avatars/')) {
        const cleanedPath = normalizedAvatar.replace(/^\/+/, '').replace(/^storage\//, '');
        return `${this.apiBaseUrl}/storage/${cleanedPath}`;
      }

      if (normalizedAvatar.startsWith('/')) {
        return `${this.apiBaseUrl}${normalizedAvatar}`;
      }

      return normalizedAvatar;
    },

    getUserAvatar(user) {
      const currentUser = user || this.user;
      const avatarSource = this.resolveAvatarSource(currentUser);
      const avatarVersion = currentUser?.avatar_version || currentUser?.avatar_updated_at || currentUser?.updated_at;

      if (avatarSource) {
        return this.withCacheBuster(this.getAvatarUrl(avatarSource, currentUser?.name), avatarVersion);
      }

      if (currentUser?.id) {
        // Try PNG first (since that's your current format), then JPG
        const formats = ['png', 'jpg', 'jpeg', 'webp'];
        const format = formats[0]; // Start with PNG

        const expectedPath = `/storage/avatars/${currentUser.id}/avatar.${format}`;
        const fullUrl = `${this.apiBaseUrl}${expectedPath}`;
        return this.withCacheBuster(fullUrl, avatarVersion);
      }

      const userName = currentUser?.name || 'User';
      return this.generateFallbackAvatar(userName);
    },

    // Alternative method that tries multiple formats synchronously
    getUserAvatarWithFormats(user, preferredFormat = 'jpg') {
      const currentUser = user || this.user;
      const avatarSource = this.resolveAvatarSource(currentUser);
      const avatarVersion = currentUser?.avatar_version || currentUser?.avatar_updated_at || currentUser?.updated_at;

      // Check for avatar in user object first
      if (avatarSource) {
        return this.withCacheBuster(this.getAvatarUrl(avatarSource, currentUser?.name), avatarVersion);
      }

      // Try the expected path with preferred format
      if (currentUser?.id) {
        const expectedPath = `/storage/avatars/${currentUser.id}/avatar.${preferredFormat}`;
        const fullUrl = `${this.apiBaseUrl}${expectedPath}`;
        return this.withCacheBuster(fullUrl, avatarVersion);
      }

      // Fallback
      const userName = currentUser?.name || 'User';
      return this.generateFallbackAvatar(userName);
    },

    // Method to handle avatar loading errors (use in templates with @error)
    onAvatarError(event, user) {
      console.warn('Avatar failed to load:', event.target.src);

      const currentUser = user || this.user;

      if (currentUser?.id) {
        const currentSrc = event.target.src;
        const formats = ['png', 'jpg', 'jpeg', 'webp'];

        // Find which format failed and try the next one
        let currentFormatIndex = -1;
        for (let i = 0; i < formats.length; i++) {
          if (currentSrc.includes(`avatar.${formats[i]}`)) {
            currentFormatIndex = i;
            break;
          }
        }

        // Try next format
        if (currentFormatIndex >= 0 && currentFormatIndex < formats.length - 1) {
          const nextFormat = formats[currentFormatIndex + 1];
          const nextPath = `${this.apiBaseUrl}/storage/avatars/${currentUser.id}/avatar.${nextFormat}`;
          console.log(`Trying next format (${nextFormat}):`, nextPath);
          event.target.src = nextPath;
          return;
        }
      }

      // All formats failed or no user ID, use fallback avatar
      const userName = currentUser?.name || 'User';
      event.target.src = this.generateFallbackAvatar(userName);
    },

    // Async version for cases where you need to test existence
    async testAvatarPath(avatarPath, userName) {
      const fullUrl = `${this.apiBaseUrl}${avatarPath}`;

      try {
        // Test if the avatar exists
        const exists = await this.checkImageExists(fullUrl);
        if (exists) {
          return fullUrl;
        } else {
          return this.generateFallbackAvatar(userName);
        }
      } catch (error) {
        return this.generateFallbackAvatar(userName);
      }
    },

    checkImageExists(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;

        // Timeout after 2 seconds
        setTimeout(() => resolve(false), 2000);
      });
    },

    generateFallbackAvatar(name) {
      // Use UI-Avatars as standard fallback when avatar is not available
      // This matches the backend implementation for consistency
      const userName = name || 'User';
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=009CAD&color=ffffff&size=120&bold=true`;
    },

    getInitials(name) {
      if (!name) return 'U';

      return name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    },

    // Enhanced version that tries multiple formats in sequence
    getUserAvatarWithFallback(user) {
      const currentUser = user || this.user;
      const avatarSource = this.resolveAvatarSource(currentUser);
      const avatarVersion = currentUser?.avatar_version || currentUser?.avatar_updated_at || currentUser?.updated_at;

      // Check for avatar in user object first
      if (avatarSource) {
        return this.withCacheBuster(this.getAvatarUrl(avatarSource, currentUser?.name), avatarVersion);
      }

      // Try expected path with jpg (most common)
      if (currentUser?.id) {
        const expectedPath = `/storage/avatars/${currentUser.id}/avatar.jpg`;
        const fullUrl = `${this.apiBaseUrl}${expectedPath}`;
        return this.withCacheBuster(fullUrl, avatarVersion);
      }

      // Fallback to generated avatar
      const userName = currentUser?.name || 'User';
      return this.generateFallbackAvatar(userName);
    },

    // Synchronous version for immediate use (kept for backward compatibility)
    getUserAvatarSync(user) {
      return this.getUserAvatar(user); // Now uses the improved synchronous logic
    },

    // Method to get avatar with error handling for templates
    getAvatarWithErrorHandling(user, event = null) {
      if (event) {
        // This is called from an error handler
        const currentUser = user || this.user;
        const userName = currentUser?.name || 'User';
        return this.generateFallbackAvatar(userName);
      } else {
        // Normal avatar getting
        return this.getUserAvatar(user);
      }
    }
  }
};
